// ManageContentUpload.js

import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './CSS/Dashboard.css';

const ManageContentUpload = ({
  backendUrl,
  resetKey,
  region = 'eu-west-1',
  bucketName = 'intoreality.tv',
  defaultFolder = 'documents/',
  distributionId = 'E2CZQARU46JAPO'
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [uploadError, setUploadError] = useState(null);

  const [files, setFiles] = useState([]);
  const [fetchingFiles, setFetchingFiles] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const [nameFilter, setNameFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });

  const [selectedFileInfo, setSelectedFileInfo] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const FILE_BASE_URL = 'https://files.intoreality.tv/';

  // Auth header for backend /upload calls (S3 PUT does NOT use this)
  const authHeader = () => ({ 'auth-token': localStorage.getItem('auth-token') });

  useEffect(() => {
    setSelectedFileInfo(null);
    setNameFilter('');
    setSortConfig({ key: '', direction: '' });
  }, [resetKey]);

  useEffect(() => {
    fetchFiles();
  }, [resetKey]);

  const handleFileChange = e => {
    setSelectedFile(e.target.files[0]);
    setUploadSuccess(null);
    setUploadError(null);
    setUploadProgress(0);
  };

  const handleUpload = async e => {
    e.preventDefault();
    if (!selectedFile) {
      alert('Please select a file to upload.');
      return;
    }

    setUploading(true);
    setUploadSuccess(null);
    setUploadError(null);

    try {
      const { data } = await axios.post(
        `${backendUrl}/upload/generate-presigned-url`,
        {
          region,
          bucketName,
          defaultFolder,
          distributionId,
          fileName: selectedFile.name,
          fileType: selectedFile.type
        },
        { headers: authHeader() }
      );
      const uploadUrl = data.uploadUrl;

      await axios.put(uploadUrl, selectedFile, {
        headers: { 'Content-Type': selectedFile.type },
        onUploadProgress: ev => {
          setUploadProgress(Math.round((ev.loaded * 100) / ev.total));
        }
      });

      setUploadSuccess(true);
      setSelectedFile(null);
      await fetchFiles();
    } catch (err) {
      console.error(err);
      setUploadError(err.response?.data?.message || 'Upload error');
    } finally {
      setUploading(false);
    }
  };

  const fetchFiles = async () => {
    setFetchingFiles(true);
    setFetchError(null);

    try {
      const { data } = await axios.get(`${backendUrl}/upload/`, {
        params: { region, bucketName, defaultFolder },
        headers: authHeader()
      });
      setFiles(
        data.files.filter(f => f.Key !== defaultFolder && !f.Key.endsWith('/'))
      );
    } catch (err) {
      console.error(err);
      setFetchError(err.response?.data?.message || 'Fetch error');
    } finally {
      setFetchingFiles(false);
    }
  };

  const sortFiles = key => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const filteredFiles = useMemo(
    () =>
      nameFilter
        ? files.filter(f =>
            f.Key.toLowerCase().includes(nameFilter.toLowerCase())
          )
        : files,
    [files, nameFilter]
  );

  const sortedFiles = useMemo(() => {
    const arr = [...filteredFiles];
    if (sortConfig.key) {
      arr.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        if (sortConfig.key === 'Size') {
          aVal = Number(aVal);
          bVal = Number(bVal);
        } else if (sortConfig.key === 'LastModified') {
          aVal = new Date(aVal);
          bVal = new Date(bVal);
        }
        if (aVal < bVal) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }
    return arr;
  }, [filteredFiles, sortConfig]);

  const handleRowClick = file => setSelectedFileInfo(file);

  const handleDeleteFile = async () => {
    if (
      !window.confirm(
        `Delete "${selectedFileInfo.Key.replace(
          defaultFolder,
          ''
        )}"? This cannot be undone.`
      )
    ) {
      return;
    }

    setDeleting(true);
    try {
      await axios.delete(`${backendUrl}/upload/delete`, {
        data: { region, bucketName, Key: selectedFileInfo.Key },
        headers: authHeader()
      });
      await fetchFiles();
      setSelectedFileInfo(null);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || 'Delete error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="form-group">
      <h2>Manage Content Upload</h2>

      {selectedFileInfo ? (
        <div className="form-group">
          <h3>
            File Details: {selectedFileInfo.Key.replace(defaultFolder, '')}
          </h3>
          <p>
            <strong>Key:</strong> {selectedFileInfo.Key}
          </p>
          <p>
            <strong>Last Modified:</strong>{' '}
            {new Date(selectedFileInfo.LastModified).toLocaleString()}
          </p>
          <p>
            <strong>Size:</strong>{' '}
            {(selectedFileInfo.Size / 1024).toFixed(2)} KB
          </p>
          <p>
            <strong>URL:</strong>{' '}
            <a
              href={`${FILE_BASE_URL}${selectedFileInfo.Key}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#9a51e2', textDecoration: 'underline' }}
            >
              {`${FILE_BASE_URL}${selectedFileInfo.Key}`}
            </a>
          </p>
          <button onClick={() => setSelectedFileInfo(null)}>
            Back to List
          </button>
          <button
            onClick={handleDeleteFile}
            disabled={deleting}
            style={{ backgroundColor: 'red' }}
          >
            {deleting ? 'Deleting…' : 'Delete File'}
          </button>
        </div>
      ) : (
        <>
          <form onSubmit={handleUpload}>
            <div>
              <label htmlFor="fileInput">Select File:</label>
              <input type="file" id="fileInput" onChange={handleFileChange} />
            </div>
            <button type="submit" disabled={uploading}>
              {uploading ? 'Uploading…' : 'Upload File'}
            </button>
            {uploadSuccess && (
              <p className="success-message">Upload successful!</p>
            )}
            {uploadError && <p className="error-message">{uploadError}</p>}
            {uploading && (
              <div className="progress-bar">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            )}
          </form>

          <h3>Files in S3 Bucket</h3>
          <div>
            <label>
              Filter by name:
              <input
                type="text"
                value={nameFilter}
                onChange={e => setNameFilter(e.target.value)}
              />
            </label>
          </div>

          {fetchingFiles ? (
            <p>Loading files…</p>
          ) : fetchError ? (
            <p className="error-message">{fetchError}</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th onClick={() => sortFiles('Key')}>File Name</th>
                  <th onClick={() => sortFiles('LastModified')}>
                    Last Modified
                  </th>
                  <th onClick={() => sortFiles('Size')}>Size (KB)</th>
                </tr>
              </thead>
              <tbody>
                {sortedFiles.map((file) => (
                  <tr
                    key={file.Key}
                    onClick={() => handleRowClick(file)}
                  >
                    <td>{file.Key.replace(defaultFolder, '')}</td>
                    <td>
                      {new Date(file.LastModified).toLocaleString()}
                    </td>
                    <td>{(file.Size / 1024).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

export default ManageContentUpload;
